import { useState, useContext, useEffect } from "react";

import { ArrowBack } from "@mui/icons-material";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { getGroupById, patchGroup, GroupResource } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams } from "react-router";

import { AuthStateContext } from "@/contexts/AuthContext";

import AlertGroupForm, { GroupFormData } from "./AlertGroupForm";

const AlertGroupUpdate = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { currentUserGroups, checkPermission, refreshUserGroups } =
    useContext(AuthStateContext);

  const [group, setGroup] = useState<GroupResource | null>(null);
  const [initialData, setInitialData] = useState<GroupFormData | undefined>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  const currentGroup = currentUserGroups.find((g) => g.id === Number(groupId));
  const canManageGroup = checkPermission("manage", "group", Number(groupId));

  useEffect(() => {
    const fetchGroup = async () => {
      if (!groupId) return;

      try {
        const response = await getGroupById({
          path: { group_id: Number(groupId) },
        });
        const groupData = response.data;
        if (groupData) {
          setGroup(groupData);
          setInitialData({
            name: groupData.name || "",
            description: groupData.description || "",
            isPublic: groupData.is_public || false,
            requiresAdmittanceModeration:
              groupData.requires_admittance_moderation || false,
            publicContacts:
              currentGroup?.public_contact &&
              currentGroup.public_contact.length > 0
                ? currentGroup.public_contact
                : [{ name: "", email: "", phone_number: "" }],
          });
        }
      } catch (error) {
        console.error("Error fetching group data", error);
        setError("Failed to load group data");
      } finally {
        setLoading(false);
      }
    };

    fetchGroup();
  }, [groupId]);

  const handleSubmit = async (data: GroupFormData) => {
    if (!groupId) return;

    setError("");

    if (!data.name || data.name.trim() === "") {
      setError("Please enter a group name");
      return;
    }

    if (data.isPublic) {
      const validContacts = data.publicContacts.filter(
        (contact) =>
          contact.name?.trim() ||
          contact.email?.trim() ||
          contact.phone_number?.trim(),
      );
      if (validContacts.length === 0) {
        setError(
          "At least one public contact is required when the group is public",
        );
        return;
      }
    }

    setSaving(true);

    try {
      const validContacts = data.isPublic
        ? data.publicContacts.filter(
            (contact) =>
              contact.name?.trim() ||
              contact.email?.trim() ||
              contact.phone_number?.trim(),
          )
        : [];

      await patchGroup({
        path: { group_id: Number(groupId) },
        body: {
          name: data.name.trim(),
          description: data.description.trim() || null,
          is_public: data.isPublic,
          requires_admittance_moderation: data.requiresAdmittanceModeration,
          public_contact: validContacts.map((c) => ({
            name: c.name || null,
            email: c.email || null,
            phone_number: c.phone_number || null,
          })),
        },
      });

      await refreshUserGroups();

      toast.success("Group updated successfully!");
      navigate(`/rubialert/manage-groups/${groupId}/`);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        "An error occurred when updating the group. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/rubialert/manage-groups/${groupId}/`);
  };

  if (!canManageGroup) {
    return (
      <Box sx={{ p: 3 }}>
        <Button onClick={handleBack} startIcon={<ArrowBack />} sx={{ mb: 3 }}>
          Back to Dashboard
        </Button>
        <Typography>You do not have permission to edit this group.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AlertGroupForm
      mode="update"
      initialData={initialData}
      loading={saving}
      error={error}
      onSubmit={handleSubmit}
      onBack={handleBack}
      title="Edit Group"
      subtitle={`Update settings for ${group?.name ?? currentGroup?.name}`}
      submitButtonText="Save Changes"
      submitButtonLoadingText="Saving..."
    />
  );
};

export default AlertGroupUpdate;
